/**
 * i18next initialization and configuration (Story 1893).
 *
 * Language detection priority (Architecture Section 21.6):
 *   1. User account language preference (stored in localStorage as 'ipa_language')
 *   2. Browser language (navigator.language)
 *   3. English ('en') fallback
 *
 * Translation files are bundled for the default language (English) and
 * lazy-loaded via i18next-http-backend for additional languages.
 *
 * @module shared/i18n/i18n-config
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import commonEn from './locales/en/common.json';
import subscriberEn from './locales/en/subscriber.json';
import carrierEn from './locales/en/carrier.json';
import brokerEn from './locales/en/broker.json';
import associationEn from './locales/en/association.json';
import adminEn from './locales/en/admin.json';
import notificationsEn from './locales/en/notifications.json';
import landingEn from '../../../landing/src/i18n/locales/en/landing.json';

/** All supported namespaces. */
export const I18N_NAMESPACES = [
  'common',
  'subscriber',
  'carrier',
  'broker',
  'association',
  'admin',
  'notifications',
  'landing',
] as const;

export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

/** Supported language codes. */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
] as const;

export const DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_STORAGE_KEY = 'ipa_language';

/** Returns true when running in development mode. */
function isDevMode(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__IPA_DEV_MODE__ === true
    );
  } catch {
    return false;
  }
}

/** Bundled English translations. */
const bundledResources = {
  en: {
    common: commonEn,
    subscriber: subscriberEn,
    carrier: carrierEn,
    broker: brokerEn,
    association: associationEn,
    admin: adminEn,
    notifications: notificationsEn,
    landing: landingEn,
  },
};

/**
 * Initializes i18next with React integration, language detection,
 * and HTTP backend for lazy-loading non-bundled languages.
 *
 * @param localesBasePath - Base path for HTTP backend to load translation
 *   files. Defaults to './locales'. Used for loading non-English languages.
 */
export function initI18n(localesBasePath = './locales'): typeof i18next {
  if (i18next.isInitialized) {
    return i18next;
  }

  void i18next
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: bundledResources,
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: 'common',
      ns: [...I18N_NAMESPACES],
      supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),

      interpolation: {
        escapeValue: false, // React already escapes output
      },

      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ['localStorage'],
      },

      backend: {
        loadPath: `${localesBasePath}/{{lng}}/{{ns}}.json`,
      },

      // Only load English from bundled resources; other languages via HTTP
      partialBundledLanguages: true,

      // Missing key handling: fall back to English (never show raw keys).
      // In dev mode, log missing keys to the console for developer awareness.
      saveMissing: isDevMode(),
      missingKeyHandler: isDevMode()
        ? (_lngs: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] Missing key: ${ns}:${key}`);
          }
        : false,

      react: {
        useSuspense: true,
      },
    });

  return i18next;
}

export { i18next };
